import { Flex } from '@chakra-ui/layout';
import type React from 'react';

import { useAiSearchContextSafe } from '../contexts/AiSearchContext';
import type { UnpackedFilter } from '../hooks/useMultiFilterEditor';
import { type FilterId, isSegmentedFilter } from '../types/filters';
import { AiGeneratingChip } from './ai-search/AiGeneratingChip';
import { EditAiSearchChip } from './ai-search/EditAiSearchChip';
import { FilterMenu } from './FilterMenu';
import { FilterTag } from './FilterTag';
import { SubFilterTag } from './SubFilterTag';

export type FilterWithDisabledState = {
  filter: UnpackedFilter;
  disabled: boolean;
};

interface FilterTagsProps {
  disabled?: boolean;
  filterTags: FilterWithDisabledState[];
  onRemoveFilter: (filterId: FilterId) => void;
}

export const FilterTags: React.FC<FilterTagsProps> = ({
  disabled: externalDisabled,
  filterTags,
  onRemoveFilter,
}) => {
  // Sort so that disabled filters are always last
  const sortedFilters = filterTags.sort((a, b) => {
    return Number(a.disabled) - Number(b.disabled);
  });

  return (
    <>
      {sortedFilters.map(({ filter, disabled }) => {
        const TagComponent = isSegmentedFilter(filter)
          ? SubFilterTag
          : FilterTag;

        return (
          <TagComponent
            key={filter.displayId || filter.id}
            filter={filter}
            onRemoveFilter={onRemoveFilter}
            disabled={disabled || externalDisabled}
            hideDisabledTooltip={externalDisabled}
          />
        );
      })}
    </>
  );
};

export const FilterTagsSupportingAISearch = (
  props: { className?: string } & Pick<
    FilterTagsProps,
    'filterTags' | 'onRemoveFilter'
  >
) => {
  const aiSearchContext = useAiSearchContextSafe();
  const isAiSearchLoading = aiSearchContext?.isAiSearchLoading ?? false;
  const canRenderAiSearchChips = aiSearchContext !== null;
  const hasVisibleFilters = props.filterTags.length > 0;

  return (
    <Flex
      flexWrap="wrap"
      alignItems="center"
      gap={2}
      className={props.className}
    >
      {canRenderAiSearchChips &&
        (isAiSearchLoading ? <AiGeneratingChip /> : <EditAiSearchChip />)}

      <FilterTags {...props} disabled={isAiSearchLoading} />
      <FilterMenu
        isDisabled={isAiSearchLoading}
        shiftLeft={hasVisibleFilters}
      />
    </Flex>
  );
};
